import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, differenceInDays } from 'date-fns'
import { Calendar, Clock, Users, ListTodo, Download, ChevronDown, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar as CalendarPicker } from '@/components/ui/calendar'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { timeEntryService } from '@/services/timeEntryService'
import { cn } from '@/lib/utils'

const formatDuration = (seconds) => {
  if (!seconds) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const formatHHMM = (seconds) => {
  if (!seconds) return '00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const formatTime12 = (dateStr) => {
  if (!dateStr) return '—'
  return format(new Date(dateStr), 'h:mmaaa')
}

const getMemberName = (user) => {
  if (!user) return 'Unknown'
  return user.employee
    ? `${user.employee.first_name} ${user.employee.last_name}`
    : user.email || 'Unknown'
}

const MEMBER_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
]

// Compute task groups from raw entries
const computeTaskGroups = (entries) => {
  const map = {}
  entries.forEach(e => {
    const key = e.task_id
    if (!map[key]) map[key] = { task: e.task, totalDuration: 0, entryCount: 0 }
    map[key].totalDuration += e.duration || 0
    map[key].entryCount++
  })
  return Object.values(map)
}

// Compute user groups from raw entries
const computeUserGroups = (entries) => {
  const map = {}
  entries.forEach(e => {
    const key = e.user_id || e.user?.id
    if (!key) return
    if (!map[key]) map[key] = { user: e.user, totalDuration: 0, taskIds: new Set() }
    map[key].totalDuration += e.duration || 0
    map[key].taskIds.add(e.task_id)
  })
  return Object.values(map).map(g => ({
    user: g.user,
    totalDuration: g.totalDuration,
    uniqueTaskCount: g.taskIds.size
  }))
}

// Daily totals for project chart
const getDailyTotals = (entries, startDate, endDate) => {
  const days = eachDayOfInterval({ start: parseISO(startDate), end: parseISO(endDate) })
  const map = {}
  days.forEach(d => { map[format(d, 'yyyy-MM-dd')] = 0 })
  entries.forEach(e => {
    const key = format(new Date(e.start_time), 'yyyy-MM-dd')
    if (map[key] !== undefined) map[key] += e.duration || 0
  })
  return days.map(d => ({
    date: format(d, 'MMM d'),
    seconds: map[format(d, 'yyyy-MM-dd')],
    hours: +(map[format(d, 'yyyy-MM-dd')] / 3600).toFixed(2)
  }))
}

// Daily totals per member for member chart (uses ALL entries, not filtered)
const getMemberDailyTotals = (entries, startDate, endDate) => {
  const days = eachDayOfInterval({ start: parseISO(startDate), end: parseISO(endDate) })
  const members = {}
  entries.forEach(e => {
    const uid = e.user_id || e.user?.id
    if (!uid) return
    if (!members[uid]) members[uid] = { id: uid, name: getMemberName(e.user), data: {} }
    const key = format(new Date(e.start_time), 'yyyy-MM-dd')
    members[uid].data[key] = (members[uid].data[key] || 0) + (e.duration || 0)
  })
  const memberList = Object.values(members)
  const chartData = days.map(d => {
    const dateKey = format(d, 'yyyy-MM-dd')
    const row = { date: format(d, 'MMM d') }
    memberList.forEach(m => {
      row[m.name] = +((m.data[dateKey] || 0) / 3600).toFixed(2)
      row[`${m.name}_seconds`] = m.data[dateKey] || 0
    })
    return row
  })
  return { chartData, memberList }
}

const ProjectChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-background border border-border rounded-md px-3 py-2 text-sm shadow-md">
      <p className="font-medium">{label}</p>
      <p className="text-muted-foreground">{formatHHMM(payload[0]?.payload?.seconds)}</p>
    </div>
  )
}

const MemberChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-background border border-border rounded-md px-3 py-2 text-sm shadow-md space-y-1">
      <p className="font-medium">{label}</p>
      {payload.filter(p => p.value > 0).map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.fill }} />
          <span className="text-muted-foreground">{p.dataKey}:</span>
          <span>{formatHHMM(p.payload[`${p.dataKey}_seconds`] ?? Math.round(p.value * 3600))}</span>
        </div>
      ))}
    </div>
  )
}

const TimeOverview = ({ projectId }) => {
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  })
  const [selectedMemberId, setSelectedMemberId] = useState('all')
  const [activitySearch, setActivitySearch] = useState('')

  const dayCount = differenceInDays(parseISO(dateRange.end), parseISO(dateRange.start)) + 1

  const { data: rawData, isLoading: loadingRaw } = useQuery(
    ['projects', String(projectId), 'time-entries', dateRange],
    () => timeEntryService.getProjectEntries(projectId, {
      start_date: dateRange.start,
      end_date: dateRange.end
    })
  )

  const allEntries = rawData?.data?.entries || []

  // All unique members from unfiltered entries (for dropdown)
  const allMembers = useMemo(() => {
    const map = {}
    allEntries.forEach(e => {
      const uid = String(e.user_id || e.user?.id)
      if (uid && !map[uid]) map[uid] = { id: uid, name: getMemberName(e.user) }
    })
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
  }, [allEntries])

  // Filtered entries based on selected member
  const filteredEntries = useMemo(() => {
    if (selectedMemberId === 'all') return allEntries
    return allEntries.filter(e => String(e.user_id || e.user?.id) === selectedMemberId)
  }, [allEntries, selectedMemberId])

  // Activity log with search filter
  const searchedEntries = useMemo(() => {
    if (!activitySearch.trim()) return filteredEntries
    const q = activitySearch.toLowerCase()
    return filteredEntries.filter(e =>
      (e.task?.title || '').toLowerCase().includes(q) ||
      getMemberName(e.user).toLowerCase().includes(q)
    )
  }, [filteredEntries, activitySearch])

  // Computed stats from filtered entries
  const taskGroups = useMemo(() => computeTaskGroups(filteredEntries), [filteredEntries])
  const userGroups = useMemo(() => computeUserGroups(filteredEntries), [filteredEntries])
  const totalSeconds = useMemo(() => filteredEntries.reduce((s, e) => s + (e.duration || 0), 0), [filteredEntries])

  // Chart data — project chart uses filtered, member chart always uses all
  const dailyTotals = useMemo(
    () => filteredEntries.length > 0 ? getDailyTotals(filteredEntries, dateRange.start, dateRange.end) : [],
    [filteredEntries, dateRange.start, dateRange.end]
  )

  const { chartData: memberChartData, memberList } = useMemo(
    () => allEntries.length > 0
      ? getMemberDailyTotals(allEntries, dateRange.start, dateRange.end)
      : { chartData: [], memberList: [] },
    [allEntries, dateRange.start, dateRange.end]
  )

  const selectedMemberName = selectedMemberId === 'all'
    ? 'All Members'
    : allMembers.find(m => m.id === selectedMemberId)?.name || 'All Members'

  const exportCSV = () => {
    const header = ['Date', 'Start Time', 'End Time', 'Task', 'Member', 'Duration (HH:MM)', 'Note']
    const rows = filteredEntries.map(e => {
      const name = getMemberName(e.user)
      return [
        format(new Date(e.start_time), 'yyyy-MM-dd'),
        formatTime12(e.start_time),
        formatTime12(e.end_time),
        e.task?.title || '',
        name,
        formatHHMM(e.duration),
        e.note || ''
      ]
    })
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `time-log-${dateRange.start}-to-${dateRange.end}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Date range */}
        <div className="flex items-center gap-1 rounded-md border bg-background px-1 py-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-2">
            <Calendar className="h-3.5 w-3.5" />
            <span>Date range</span>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="sm" className="h-7 text-xs gap-1">
                {format(parseISO(dateRange.start), 'MMM d')} – {format(parseISO(dateRange.end), 'MMM d, yyyy')}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3">
                <CalendarPicker
                  mode="range"
                  selected={{ from: parseISO(dateRange.start), to: parseISO(dateRange.end) }}
                  onSelect={(range) => {
                    if (range?.from) {
                      setDateRange({
                        start: format(range.from, 'yyyy-MM-dd'),
                        end: range.to ? format(range.to, 'yyyy-MM-dd') : format(range.from, 'yyyy-MM-dd')
                      })
                    }
                  }}
                  numberOfMonths={2}
                />
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground px-2"
            onClick={() => setDateRange({
              start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
              end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
            })}
          >
            This month
          </Button>
        </div>

        {/* Member filter */}
        <div className="flex items-center gap-1 rounded-md border bg-background px-1 py-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-2">
            <Users className="h-3.5 w-3.5" />
            <span>Member</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="h-7 text-xs gap-1 max-w-[160px]">
                <span className="truncate">{selectedMemberName}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem
                onClick={() => setSelectedMemberId('all')}
                className={cn(selectedMemberId === 'all' && 'bg-muted font-medium')}
              >
                <Users className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                All Members
              </DropdownMenuItem>
              {allMembers.length > 0 && <DropdownMenuSeparator />}
              {allMembers.map((m) => (
                <DropdownMenuItem
                  key={m.id}
                  onClick={() => setSelectedMemberId(m.id)}
                  className={cn(selectedMemberId === m.id && 'bg-muted font-medium')}
                >
                  <Avatar className="h-5 w-5 mr-2 shrink-0">
                    <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                      {m.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{m.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Clock className="h-4 w-4" /> Total Time
          </div>
          <p className="text-2xl font-semibold">{loadingRaw ? '—' : formatDuration(totalSeconds)}</p>
        </div>
        <div className="rounded-lg border p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Users className="h-4 w-4" /> Contributors
          </div>
          <p className="text-2xl font-semibold">{loadingRaw ? '—' : userGroups.length}</p>
        </div>
        <div className="rounded-lg border p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <ListTodo className="h-4 w-4" /> Tasks Tracked
          </div>
          <p className="text-2xl font-semibold">{loadingRaw ? '—' : taskGroups.length}</p>
        </div>
      </div>

      {/* Bar Chart with Project / Member tabs */}
      {loadingRaw ? (
        <Skeleton className="h-52 w-full rounded-lg" />
      ) : allEntries.length > 0 ? (
        <Tabs defaultValue="project">
          <div className="rounded-lg border">
            <div className="px-3 py-3 border-b">
              <TabsList className="h-8">
                <TabsTrigger value="project" className="text-xs h-7">Project</TabsTrigger>
                <TabsTrigger value="member" className="text-xs h-7">Member</TabsTrigger>
              </TabsList>
            </div>

            {/* Project chart — respects member filter */}
            <TabsContent value="project" className="p-4 mt-0">
              {dailyTotals.some(d => d.seconds > 0) ? (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={dailyTotals} barSize={dayCount > 14 ? 8 : 20}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      interval={dayCount > 20 ? Math.floor(dayCount / 10) : 0}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v}h`}
                      width={32}
                    />
                    <Tooltip content={<ProjectChartTooltip />} cursor={{ fill: 'hsl(var(--muted))', radius: 4 }} />
                    <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">No data for selected member.</p>
              )}
            </TabsContent>

            {/* Member chart — always shows all members */}
            <TabsContent value="member" className="p-4 mt-0">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={memberChartData} barSize={dayCount > 14 ? 6 : 16}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    interval={dayCount > 20 ? Math.floor(dayCount / 10) : 0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}h`}
                    width={32}
                  />
                  <Tooltip content={<MemberChartTooltip />} cursor={{ fill: 'hsl(var(--muted))', radius: 4 }} />
                  {memberList.map((m, i) => (
                    <Bar
                      key={m.id}
                      dataKey={m.name}
                      stackId="members"
                      fill={MEMBER_COLORS[i % MEMBER_COLORS.length]}
                      radius={i === memberList.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              {memberList.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t">
                  {memberList.map((m, i) => (
                    <div key={m.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: MEMBER_COLORS[i % MEMBER_COLORS.length] }} />
                      {m.name}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      ) : null}

      {/* Work Summary & Activity Log tabs */}
      <Tabs defaultValue="work-summary">
        <div className="flex items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="work-summary">Work Summary</TabsTrigger>
            <TabsTrigger value="activity-log">Activity Log</TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportCSV}>
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>

        {/* Work Summary */}
        <TabsContent value="work-summary" className="mt-4">
          {loadingRaw ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">By Task</h3>
                {taskGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No time logged in this period.</p>
                ) : (
                  <div className="space-y-1">
                    {[...taskGroups].sort((a, b) => b.totalDuration - a.totalDuration).map((g) => (
                      <div key={g.task?.id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors">
                        <span className="text-sm truncate flex-1">{g.task?.title || 'Unknown task'}</span>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0 ml-2">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="font-medium text-foreground tabular-nums">{formatDuration(g.totalDuration)}</span>
                          <span className="text-xs">({g.entryCount} {g.entryCount === 1 ? 'entry' : 'entries'})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold">By Person</h3>
                {userGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No time logged in this period.</p>
                ) : (
                  <div className="space-y-1">
                    {[...userGroups].sort((a, b) => b.totalDuration - a.totalDuration).map((g) => {
                      const name = getMemberName(g.user)
                      return (
                        <div key={g.user?.id} className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-muted/50 transition-colors">
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{name.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm flex-1 truncate">{name}</span>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="font-medium text-foreground tabular-nums">{formatDuration(g.totalDuration)}</span>
                            <span className="text-xs">({g.uniqueTaskCount} task{g.uniqueTaskCount !== 1 ? 's' : ''})</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Activity Log */}
        <TabsContent value="activity-log" className="mt-4">
          {loadingRaw ? (
            <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <div className="space-y-3">
              {/* Search bar */}
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by task or member..."
                  value={activitySearch}
                  onChange={(e) => setActivitySearch(e.target.value)}
                  className="w-full h-8 pl-8 pr-8 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                />
                {activitySearch && (
                  <button
                    onClick={() => setActivitySearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {searchedEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {activitySearch ? 'No results found.' : 'No activity in this period.'}
                </p>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <div className={cn(searchedEntries.length > 20 && "max-h-[560px] overflow-y-auto")}>
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[72px]">Date</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[140px]">Time</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Task</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden sm:table-cell">Member</th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground w-20">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchedEntries.map((entry, idx) => {
                          const entryDate = format(new Date(entry.start_time), 'MMM d')
                          const prevDate = idx > 0 ? format(new Date(searchedEntries[idx - 1].start_time), 'MMM d') : null
                          const showDate = entryDate !== prevDate
                          const timeStr = `${formatTime12(entry.start_time)} – ${formatTime12(entry.end_time)}`
                          const name = getMemberName(entry.user)

                          return (
                            <tr
                              key={entry.id}
                              className={cn(
                                "border-b last:border-0 hover:bg-muted/30 transition-colors",
                                showDate && idx > 0 && "border-t-2 border-t-muted"
                              )}
                            >
                              <td className="px-3 py-2 text-muted-foreground tabular-nums text-xs font-medium">
                                {showDate ? entryDate : ''}
                              </td>
                              <td
                                className={cn(
                                  "px-3 py-2 tabular-nums text-muted-foreground cursor-default truncate",
                                  dayCount > 7 ? "text-xs max-w-[110px]" : "text-sm"
                                )}
                                title={timeStr}
                              >
                                {timeStr}
                              </td>
                              <td className="px-3 py-2 truncate max-w-[180px]">
                                {entry.task?.title || <span className="text-muted-foreground italic">Unnamed activity</span>}
                              </td>
                              <td className="px-3 py-2 hidden sm:table-cell">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-5 w-5 shrink-0">
                                    <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                                      {name.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="truncate text-sm">{name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">
                                {formatHHMM(entry.duration)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {searchedEntries.length > 20 && (
                    <div className="px-3 py-2 border-t bg-muted/20 text-xs text-muted-foreground text-right">
                      {searchedEntries.length} records
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default TimeOverview
